import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useInView } from "@/hooks/useInView";
import { ChevronDown, HelpCircle } from "lucide-react";

const FAQSection = () => {
  const { t, lang } = useI18n();
  const [headerRef, headerInView] = useInView();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [1, 2, 3, 4, 5, 6];

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-16 md:py-28 bg-navy relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,hsl(var(--cream)/0.03),transparent_70%)] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center mb-10 md:mb-16 anim-fade-up ${headerInView ? "in-view" : ""}`}>
          <span className="text-gold text-xs md:text-sm font-bold tracking-widest uppercase">{t("faq.label")}</span>
          <h2 className={`text-2xl md:text-5xl font-black text-cream mt-2 ${lang === "en" ? "font-houstiq" : ""}`}>
            {t("faq.title")}
          </h2>
          <div className="w-10 md:w-16 h-0.5 md:h-1 bg-cream/20 mx-auto mt-3 md:mt-4 rounded-full" />
        </div>

        <div className="max-w-3xl mx-auto space-y-3 md:space-y-4">
          {faqs.map((num, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={num}
                className={`bg-cream/5 border rounded-xl md:rounded-2xl overflow-hidden transition-all duration-300 ${
                  isOpen ? "border-gold/30 bg-cream/8" : "border-cream/10 hover:border-cream/20"
                } anim-fade-up ${headerInView ? "in-view" : ""}`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center gap-3 md:gap-4 px-5 md:px-7 py-4 md:py-5 text-start"
                >
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    isOpen ? "bg-gold text-navy" : "bg-cream/10 text-cream/50"
                  }`}>
                    <HelpCircle size={16} />
                  </div>
                  <span className={`flex-1 text-sm md:text-base font-bold transition-colors ${
                    isOpen ? "text-cream" : "text-cream/80"
                  }`}>
                    {t(`faq.${num}.q`)}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-cream/40 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-gold" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="px-5 md:px-7 pb-5 md:pb-6 text-cream/50 text-sm leading-relaxed ps-16 md:ps-[76px]">
                    {t(`faq.${num}.a`)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
